<div
  class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
>
  <!-- Toolbar -->
  <div
    class="px-6 py-4 border-b border-gray-100 bg-white flex flex-col lg:flex-row items-start lg:items-center gap-3"
  >
    <h4 class="text-lg font-bold text-gray-800 m-0 shrink-0">Vouchers List</h4>

    <!-- Action buttons -->
    <div class="flex items-center gap-3 shrink-0 ml-auto" *ngIf="!disabled">
      <!-- Quote mode: calculator + reset + Add Template -->

      <!-- Quick-add dropdown -->
      <div *ngIf="!disabled" class="w-full lg:w-80">
        <app-input-dropdown-search
          #quickAddDropdown
          [options]="voucherOptions"
          [ngModel]="quickAddVoucherId"
          [ngModelOptions]="{ standalone: true }"
          optionLabel="displayName"
          optionSelectedLabel="name"
          optionValue="id"
          placeholder="Search and add voucher..."
          dropdownIcon="pi pi-plus"
          [showClear]="false"
          (valueSelected)="quickAddVoucher($event)"
          (createClicked)="quickCreateAndAdd()"
        ></app-input-dropdown-search>
      </div>

      <ng-container *ngIf="mode === 'quote'">
        <button
          pButton
          icon="pi pi-calculator"
          class="w-10 h-10 border rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 border-gray-300"
          pTooltip="Calculate Vouchers"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [disabled]="!vouchers?.length"
          (click)="calculateRequested.emit()"
        ></button>

        <button
          pButton
          icon="pi pi-trash"
          class="w-10 h-10 border rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 border-gray-300"
          pTooltip="Remove All"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [disabled]="!vouchers?.length"
          (click)="removeAll()"
        ></button>

        <button
          type="button"
          class="flex items-center gap-2 hover:bg-gray-100 border border-gray-300 text-gray-600 font-bold text-sm px-4 py-2 rounded-lg transition-all active:bg-gray-200"
          (click)="openTemplateSearch()"
        >
          <i class="pi pi-plus"></i>
          <span>Template</span>
        </button>
      </ng-container>

      <!-- Template mode: reset only -->
      <ng-container *ngIf="mode === 'template'">
        <button
          pButton
          icon="pi pi-trash"
          class="p-button-text p-button-secondary w-10 h-10 border rounded-full hover:bg-gray-50"
          pTooltip="Remove All"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [disabled]="!vouchers?.length"
          (click)="removeAll()"
        ></button>
      </ng-container>
    </div>
  </div>

  <!-- Body -->
  <div class="p-4">
    <!-- Grouped vouchers -->
    <div *ngFor="let group of groupedVouchers" class="mb-8 last:mb-0">
      <div class="flex items-center gap-2 mb-4 ml-2">
        <div
          class="w-[2px] h-4 bg-[var(--primary-500)] rounded-full shrink-0"
        ></div>
        <span
          class="text-xs font-bold uppercase tracking-widest text-gray-500"
          >{{ group.typeName }}</span
        >
        <span
          class="text-xs font-bold text-[var(--primary-500)] bg-[var(--primary-50)] px-1.5 py-0.5 rounded-full shrink-0"
          >{{ group.vouchers.length }}</span
        >
        <div class="flex-1 h-px bg-gray-100"></div>
      </div>

      <div class="flex flex-col gap-1">
        <div
          *ngFor="let voucher of group.vouchers"
          [@slideIn]
          class="group flex flex-col lg:flex-row items-end lg:items-center gap-4 p-1 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-100"
        >
          <!-- Voucher selector -->
          <div [class]="isSameCurrency ? 'w-full lg:w-1/2 min-w-[200px]' : 'w-full lg:w-1/3 min-w-[200px]'">
            <app-input-dropdown-search
              [options]="voucherOptions"
              [(ngModel)]="voucher.voucherId"
              [ngModelOptions]="{ standalone: true }"
              optionLabel="displayName"
              optionSelectedLabel="name"
              optionValue="id"
              placeholder="Select a voucher..."
              [disabled]="disabled"
              [showClear]="true"
              (ngModelChange)="onVoucherSelected(voucher, $event)"
              (panelHiddenWithoutSelection)="onVoucherBlur()"
              (createClicked)="openCreateVoucherModal(voucher)"
            ></app-input-dropdown-search>
          </div>

          <!-- Single currency: only local amount -->
          <div *ngIf="isSameCurrency" class="flex-1 w-full relative">
            <app-input-number
              type="currency"
              [currencyType]="localCurrency?.acronym || 'BRL'"
              [placeholder]="getCurrencyPlaceholder(localCurrency)"
              [ngModel]="voucher[localField]"
              [ngModelOptions]="{ standalone: true }"
              [readonly]="disabled"
              (userChange)="onLocalAmountInput(voucher, $event)"
            ></app-input-number>
          </div>

          <!-- Dual currency: local input + toggle + reference input -->
          <div *ngIf="!isSameCurrency" class="flex-1 w-full flex items-center gap-2">
            <!-- Local-side input -->
            <div class="relative flex-1">
              <app-input-number
                type="currency"
                [currencyType]="localCurrency?.acronym || 'BRL'"
                [placeholder]="getCurrencyPlaceholder(localCurrency)"
                [ngModel]="voucher[localField]"
                [ngModelOptions]="{ standalone: true }"
                [readonly]="isReferenceCurrency(voucher) || disabled"
                (userChange)="onLocalAmountInput(voucher, $event)"
              ></app-input-number>
            </div>

            <!-- Currency toggle: two buttons -->
            <div class="flex items-center bg-gray-100 rounded-md p-1 shrink-0 h-[38px]">
              <button
                type="button"
                class="min-w-[52px] px-3 h-full rounded text-xs font-bold transition-all flex items-center justify-center gap-1"
                [ngClass]="!isReferenceCurrency(voucher) ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'"
                (click)="setCurrencyById(voucher, idLocalCurrency)"
                [disabled]="disabled"
              >
                <img *ngIf="localCurrency?.flag" [src]="'assets/img/flags/' + localCurrency.flag + '.png'" class="h-3" />
                {{ localCurrency?.acronym || "BRL" }}
              </button>
              <button
                type="button"
                class="min-w-[52px] px-3 h-full rounded text-xs font-bold transition-all flex items-center justify-center gap-1"
                [ngClass]="isReferenceCurrency(voucher) ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'"
                (click)="referenceCurrency && setCurrencyById(voucher, idReferenceCurrency)"
                [disabled]="disabled"
              >
                <img *ngIf="referenceCurrency?.flag" [src]="'assets/img/flags/' + referenceCurrency.flag + '.png'" class="h-3" />
                {{ referenceCurrency?.acronym || "?" }}
              </button>
            </div>

            <!-- Reference-side input -->
            <div class="relative flex-1">
              <app-input-number
                type="currency"
                [currencyType]="referenceCurrency?.acronym || 'USD'"
                [placeholder]="getCurrencyPlaceholder(referenceCurrency)"
                [ngModel]="voucher[referenceField]"
                [ngModelOptions]="{ standalone: true }"
                [readonly]="!isReferenceCurrency(voucher) || disabled"
                (userChange)="onReferenceAmountInput(voucher, $event)"
              ></app-input-number>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1 pl-2 mb-1 lg:mb-0">
            <button
              pButton
              [icon]="
                voucher.remark
                  ? 'fas fa-solid fa-comment'
                  : 'far fa-regular fa-comment'
              "
              [disabled]="disabled"
              class="p-button-text p-button-rounded w-9 h-9 transition-all duration-200"
              [pTooltip]="voucher.remark ? voucher.remark : 'Add Remark'"
              tooltipPosition="top"
              tooltipStyleClass="custom-tooltip"
              [ngClass]="{
                'text-[var(--primary-500)] bg-blue-50 shadow-sm':
                  voucher.remark,
                'text-gray-400': !voucher.remark,
              }"
              (click)="editRemark(voucher)"
            ></button>
            <button
              *ngIf="!disabled"
              pButton
              icon="pi pi-trash"
              class="p-button-text p-button-danger p-button-rounded w-9 h-9"
              pTooltip="Remove"
              tooltipPosition="top"
              tooltipStyleClass="custom-tooltip"
              (click)="removeVoucher(voucher.id)"
            ></button>
          </div>
        </div>
      </div>
    </div>
    <!-- Empty state -->
    <div
      *ngIf="groupedVouchers.length === 0"
      class="flex flex-col items-center justify-center py-3 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200"
    >
      <i class="pi pi-inbox text-2xl mb-1 opacity-20"></i>
      <p class="text-xs font-medium">No vouchers added yet</p>
    </div>
  </div>
</div>
